import axios from "axios";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { API, BACKEND_URL, FREE_BACKEND_CONFIG } from "@/config/runtime";

export { API, BACKEND_URL };

export const getPublicInstitutionalContentUrl = (productId) =>
  `${API}/public/products/${encodeURIComponent(productId)}/content`;

const defaultAxiosAdapter = axios.getAdapter(axios.defaults.adapter);
const NATIVE_HTTP_UNSUPPORTED_RESPONSE_TYPES = new Set(["blob", "arraybuffer", "stream"]);

function isFormDataPayload(data) {
  return typeof FormData !== "undefined" && data instanceof FormData;
}

function removeHeader(headers, name) {
  if (!headers) return;
  if (typeof headers.delete === "function") {
    headers.delete(name);
    headers.delete(name.toLowerCase());
    return;
  }
  delete headers[name];
  delete headers[name.toLowerCase()];
}

function toPlainHeaders(headers = {}) {
  const source = typeof headers.toJSON === "function" ? headers.toJSON() : headers;
  return Object.entries(source || {}).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) acc[key] = String(value);
    return acc;
  }, {});
}

function resolveRequestUrl(config) {
  const rawUrl = String(config.url || "");
  if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
  const baseUrl = String(config.baseURL || API).replace(/\/+$/, "");
  return `${baseUrl}/${rawUrl.replace(/^\/+/, "")}`;
}

function createAxiosLikeError(message, config, response, code) {
  const error = new Error(message);
  error.config = config;
  error.response = response;
  if (code) error.code = code;
  return error;
}

async function nativeHttpAdapter(config) {
  const data = config.data;
  const isMultipart = isFormDataPayload(data);
  const responseType = config.responseType;

  if (!Capacitor.isNativePlatform() || isMultipart || NATIVE_HTTP_UNSUPPORTED_RESPONSE_TYPES.has(responseType)) {
    return defaultAxiosAdapter(config);
  }

  const headers = toPlainHeaders(config.headers);
  const contentType = headers["Content-Type"] || headers["content-type"] || "";
  let requestData = data;
  if (typeof requestData === "string" && contentType.includes("application/json")) {
    try {
      requestData = JSON.parse(requestData);
    } catch (error) {
      // Keep non-JSON strings as-is.
    }
  }

  try {
    const nativeResponse = await CapacitorHttp.request({
      url: resolveRequestUrl(config),
      method: String(config.method || "get").toUpperCase(),
      headers,
      params: config.params || undefined,
      data: requestData,
      connectTimeout: config.timeout || 90000,
      readTimeout: config.timeout || 90000,
    });

    const response = {
      data: nativeResponse.data,
      status: nativeResponse.status,
      statusText: String(nativeResponse.status || ""),
      headers: nativeResponse.headers || {},
      config,
      request: null,
    };

    const validateStatus = config.validateStatus || ((status) => status >= 200 && status < 300);
    if (!validateStatus(response.status)) {
      throw createAxiosLikeError(`Request failed with status code ${response.status}`, config, response);
    }
    return response;
  } catch (error) {
    if (error.response) throw error;
    throw createAxiosLikeError(error?.message || "Network Error", config, null, error?.code || "ERR_NETWORK");
  }
}

const apiClient = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 90000,
  adapter: nativeHttpAdapter,
});

// Request interceptor pour ajouter le token
apiClient.interceptors.request.use(
  (config) => {
    if (isFormDataPayload(config.data)) {
      removeHeader(config.headers, "Content-Type");
      removeHeader(config.headers, "content-type");
    }

    const token = localStorage.getItem("mmj_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor pour gérer les erreurs
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const method = (error.config?.method || "").toLowerCase();
    const canRetry = ["get", "head", "options"].includes(method);
    const isNetworkFailure = !error.response && (error.message === "Network Error" || error.code === "ECONNABORTED");
    if (isNetworkFailure && canRetry && !error.config.__mmjRetry) {
      error.config.__mmjRetry = true;
      await new Promise((resolve) => setTimeout(resolve, 1500));
      return apiClient(error.config);
    }

    if (error.response?.status === 401) {
      localStorage.removeItem("mmj_token");
      localStorage.removeItem("mmj_user");
      window.location.href = "/login";
    }
    // Normalize detail to string to prevent React rendering crashes
    if (error.response?.data?.detail && typeof error.response.data.detail !== "string") {
      const d = error.response.data.detail;
      error.response.data.detail = Array.isArray(d)
        ? d.map((e) => (typeof e === "object" ? e.msg || JSON.stringify(e) : String(e))).join("; ")
        : typeof d === "object" ? (d.msg || d.message || JSON.stringify(d)) : String(d);
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => apiClient.post("/auth/register", data),
  login: (data) => apiClient.post("/auth/login", data),
  getMe: () => apiClient.get("/auth/me"),
  requestEmailVerification: (data) => apiClient.post("/auth/email-verification/request", data),
  confirmEmailVerification: (data) => apiClient.post("/auth/email-verification/confirm", data),
  forgotPassword: (data) => apiClient.post("/auth/forgot-password", data),
  resetPassword: (data) => apiClient.post("/auth/reset-password", data),
  passwordSupport: (data) => apiClient.post("/auth/password-support", data),
};

export const profileAPI = {
  getMe: () => apiClient.get("/profile/me"),
  updatePhoto: (formData) => apiClient.post("/profile/photo", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: FREE_BACKEND_CONFIG.uploadTimeoutMs,
  }),
  getPhotoUrl: (version = "") => {
    const token = localStorage.getItem("mmj_token");
    return `${API}/profile/photo?auth=${token}${version ? `&v=${encodeURIComponent(version)}` : ""}`;
  },
  requestAccountDeletion: (data) => apiClient.post("/account/deletion-request", data),
};

// Products API
export const productsAPI = {
  list: (params) => apiClient.get("/products", { params }),
  getById: (id) => apiClient.get(`/products/${id}`),
  create: (data) => apiClient.post("/products", data),
  createInstitutional: (data) => apiClient.post("/institutional-products", data),
  update: (productId, data) => apiClient.patch(`/products/${productId}`, data),
  uploadFile: (productId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.post(`/products/${productId}/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  uploadCover: (productId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.post(`/products/${productId}/cover`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  uploadRightsProof: (productId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.post(`/products/${productId}/rights-proof`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  validate: (productId, data) => apiClient.patch(`/products/${productId}/validate`, data),
  getPreviewUrl: (productId) => {
    const token = localStorage.getItem("mmj_token");
    return `${API}/products/${productId}/preview?auth=${token}`;
  },
};

export const authorAPI = {
  listProducts: () => apiClient.get("/author/products"),
  getProductSalesStats: (productId, params) => apiClient.get(`/author/products/${productId}/sales-stats`, { params }),
};

export const messagesAPI = {
  listConversations: () => apiClient.get("/messages/conversations"),
  createConversation: (data) => apiClient.post("/messages/conversations", data),
  getMessages: (conversationId) => apiClient.get(`/messages/conversations/${conversationId}/messages`),
  sendMessage: (conversationId, body) => apiClient.post(`/messages/conversations/${conversationId}/messages`, { body }),
  blockUser: (blockedUserId) => apiClient.post("/messages/block-user", { blocked_user_id: blockedUserId }),
  report: (data) => apiClient.post("/moderation/reports", data),
};

export const libraryAPI = {
  list: () => apiClient.get("/library"),
  sync: () => apiClient.get("/library/sync"),
  saveProgress: (data) => apiClient.post("/library/progress", data),
  setFavorite: (productId, favorite) => apiClient.post("/library/favorites", { product_id: productId, favorite }),
  getStreamUrl: (productId) => {
    const token = localStorage.getItem("mmj_token");
    return `${API}/products/${productId}/preview?auth=${token}`;
  },
  getStreamEndpoint: (productId) => `${API}/products/${encodeURIComponent(productId)}/preview`,
  registerOfflineDevice: (data) => apiClient.post("/library/offline-devices", data),
  getOfflinePackageEndpoint: (productId) => `${API}/products/${encodeURIComponent(productId)}/offline-package`,
  getProtectedPdfMeta: (productId) => apiClient.get(`/products/${encodeURIComponent(productId)}/protected-pages/meta`),
  getProtectedPdfPageUrl: (productId, pageNumber) => {
    const token = localStorage.getItem("mmj_token");
    return `${API}/products/${encodeURIComponent(productId)}/protected-pages/${pageNumber}?auth=${token}`;
  },
  getSourceUrl: (productId) => {
    const token = localStorage.getItem("mmj_token");
    return `${API}/library/products/${productId}/source?auth=${token}`;
  },
};

// Orders API
export const ordersAPI = {
  list: (params = { order_type: "product" }) => apiClient.get("/order-batches", { params }),
  create: (data) => apiClient.post("/orders", data),
  createBatch: (data) => apiClient.post("/orders/batch", data),
  validateOrder: (orderId, approved = true, reason = "") =>
    apiClient.post(`/orders/${orderId}/validate?approved=${approved}&rejection_reason=${encodeURIComponent(reason || "")}`),
  deleteBatchFlow: (batchId, flow) =>
    apiClient.post(`/batches/${encodeURIComponent(batchId)}/delete?flow=${encodeURIComponent(flow)}`),
  approveDeletionRequest: (requestId) =>
    apiClient.post(`/order-deletion-requests/${encodeURIComponent(requestId)}/approve`),
  uploadPaymentProof: (batchId, file, proofType = "product", metadata = {}) => {
  const formData = new FormData();

  formData.append("file", file);
  Object.entries(metadata || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      formData.append(key, value);
    }
  });

  return apiClient.post(
    `/batches/${batchId}/payment-proof?proof_type=${encodeURIComponent(proofType)}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
},
  getReceiptUrl: (orderId) => {
    const token = localStorage.getItem("mmj_token");
    return `${API}/orders/${orderId}/receipt?auth=${token}`;
  },
  getBatchReceiptUrl: (order_ids) => {
  const token = localStorage.getItem("mmj_token");

  const params = Array.isArray(order_ids)
    ? order_ids
        .map(id => `order_ids=${encodeURIComponent(id)}`)
        .join("&")
    : `order_ids=${encodeURIComponent(order_ids)}`;

  return `${API}/orders/batch-receipt?${params}&auth=${token}`;
},
};

// Public API (no auth)
export const publicAPI = {
  getCatalogue: () => apiClient.get("/public/catalogue"),
  getPlatformSettings: () => apiClient.get("/public/platform-settings"),
  searchUser: (searchId) => apiClient.get(`/users/search/${encodeURIComponent(searchId)}`),
  requestAccountDeletion: (data) => apiClient.post("/public/account-deletion-request", data),
  submitContactInquiry: (data) => apiClient.post("/public/contact-inquiry", data),
};

// Reports API
export const reportsAPI = {
  getMonthlyUrl: (month) => {
    const token = localStorage.getItem("mmj_token");
    return `${API}/reports/monthly/${month}?auth=${token}`;
  },
};

// Team API
export const teamAPI = {
  getGenealogy: () => apiClient.get("/team/genealogy"),
  getMembers: () => apiClient.get("/team/members"),
  getDescendantCoaching: () => apiClient.get("/partner/descendants/coaching"),
};

// Bonuses API
export const bonusesAPI = {
  list: (params) => apiClient.get("/bonuses", { params }),
  getSummary: (params) => apiClient.get("/bonuses/summary", { params }),
  getMonthlyArchives: () => apiClient.get("/bonuses/monthly-archives"),
};

export const withdrawalsAPI = {
  getSummary: () => apiClient.get("/withdrawals/summary"),
  list: () => apiClient.get("/withdrawals"),
  create: (data) => apiClient.post("/withdrawals", data),
  cancel: (withdrawalId) => apiClient.post(`/withdrawals/${withdrawalId}/cancel`),
};

// Admin API
export const adminAPI = {
  getStats: () => apiClient.get("/admin/stats"),
  getPartners: () => apiClient.get("/admin/partners"),
  listPartnerBase: () => apiClient.get("/admin/partners/base"),
  getPartnerLineage: (partnerId) => apiClient.get(`/admin/partners/${partnerId}/lineage`),
  getPartnerProfile: (partnerId) => apiClient.get(`/admin/partners/${partnerId}/profile`),
  proposePartnerKycUpdate: (partnerId, data) => apiClient.post(`/admin/partners/${partnerId}/kyc-update`, data),
  requestPartnerDelete: (partnerId) => apiClient.post(`/admin/partners/${partnerId}/delete-request`),
  listUsers: () => apiClient.get("/admin/users"),
  getUserProfile: (userId) => apiClient.get(`/admin/users/${encodeURIComponent(userId)}/profile`),
  getNextAdminId: () => apiClient.get("/admin/users/next-id"),
  createUser: (data) => apiClient.post("/admin/users", data),
  updateUserRank: (userId, data) => apiClient.patch(`/admin/users/${userId}/rank`, data),
  listCriticalChanges: () => apiClient.get("/admin/critical-changes"),
  approveCriticalChange: (changeId, notes = "") => apiClient.post(`/admin/critical-changes/${changeId}/approve`, { notes }),
  deleteUser: (userId, password) => apiClient.delete(`/admin/users/${userId}`, { data: { password } }),
  getUserHistory: (userId) => apiClient.get(`/admin/users/${userId}/history`),
  recalculateAll: () => apiClient.post("/admin/recalculate-all", {}, { timeout: 120000 }),
  resetMonthlyActivations: () => apiClient.post("/admin/reset-monthly-activations"),
  getFounderDashboard: () => apiClient.get("/admin/founder/dashboard"),
  getArticoCash: (params) => apiClient.get("/admin/artico/cash", { params }),
  getAdminAuditPdf: (params) =>
    apiClient.get("/admin/reports/admin-audits.pdf", { params, responseType: "blob", timeout: 120000 }),
  getPlatformSettings: () => apiClient.get("/admin/platform-settings"),
  proposePlatformSettings: (data) => apiClient.patch("/admin/platform-settings", data),
  getModerationQueue: (params) => apiClient.get("/admin/moderation/queue", { params }),
  reviewAccountDeletion: (requestId, data = {}) => apiClient.post(`/admin/moderation/account-deletion/${requestId}/review`, data),
  resolveAccountDeletion: (requestId, data = {}) => apiClient.post(`/admin/moderation/account-deletion/${requestId}/resolve`, data),
  rejectAccountDeletion: (requestId, data = {}) => apiClient.post(`/admin/moderation/account-deletion/${requestId}/reject`, data),
  reviewModerationReport: (reportId, data = {}) => apiClient.post(`/admin/moderation/reports/${reportId}/review`, data),
  resolveModerationReport: (reportId, data = {}) => apiClient.post(`/admin/moderation/reports/${reportId}/resolve`, data),
  getSystemHealth: () => apiClient.get("/admin/system/health"),
  getSystemMonitoring: () => apiClient.get("/admin/system/monitoring"),
  listFailedOperations: () => apiClient.get("/admin/system/failed-operations"),
  retryFailedOperation: (orderId) => apiClient.post(`/admin/system/failed-operations/${orderId}/retry`),
  getFinancialAudit: (params) => apiClient.get("/admin/system/financial-audit", { params }),
  listBonusMonthClosures: () => apiClient.get("/admin/bonus-months/closures"),
  closeBonusMonth: (month) => apiClient.post(`/admin/bonus-months/${encodeURIComponent(month)}/close`),
  listWithdrawals: (params) => apiClient.get("/admin/withdrawals", { params }),
  processWithdrawal: (withdrawalId, notes = "") => apiClient.post(`/admin/withdrawals/${withdrawalId}/process`, { notes }),
  rejectWithdrawal: (withdrawalId, reason) => apiClient.post(`/admin/withdrawals/${withdrawalId}/reject`, { reason }),
  payWithdrawal: (withdrawalId, formData) => apiClient.post(`/admin/withdrawals/${withdrawalId}/pay`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: FREE_BACKEND_CONFIG.uploadTimeoutMs,
  }),
  exportTransactions: (params) =>
    apiClient.get("/admin/export/transactions", { params, responseType: "blob", timeout: 120000 }),
  exportFinancialReport: (params) =>
    apiClient.get("/admin/reports/financial", { params, responseType: "blob", timeout: 120000 }),
};

// Partner API
export const partnerAPI = {
  activatePAR: () => apiClient.post("/partner/activate-par"),
  getActivationStatus: () => apiClient.get("/partner/activation-status"),
  getStartupChecklist: () => apiClient.get("/partner/startup-checklist"),
  updateStartupChecklist: (data) => apiClient.put("/partner/startup-checklist", data),
  getDescendantCoaching: () => apiClient.get("/partner/descendants/coaching"),
  requestActivation: () => apiClient.post("/activation/request"),
  listActivationOrders: () => apiClient.get("/activation/orders"),
  getWalletTransactions: () => apiClient.get("/wallet/transactions"),
  listActivationBatches: () => apiClient.get("/activation/batches"),
  uploadActivationProof: (batchId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.post(`/batches/${batchId}/payment-proof?proof_type=activation`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  validateBatchActivation: (batchId, file) => {
  if (!file) {
    return apiClient.post(`/activation/batch/${batchId}/validate`);
  }

  const formData = new FormData();

  formData.append("file", file);

  return apiClient.post(
    `/activation/batch/${batchId}/validate`,
    formData,
    {
      headers: {
        "Content-Type":
          "multipart/form-data",
      },
    }
  );
},  
};

// Notifications API
export const notificationsAPI = {
  list: () => apiClient.get("/notifications"),
  markRead: (id) => apiClient.patch(`/notifications/${id}/read`),
  markAllRead: () => apiClient.patch("/notifications/read-all"),
};

// KYC API
export const kycAPI = {
  submit: (formData) => apiClient.post("/kyc/submit", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: FREE_BACKEND_CONFIG.uploadTimeoutMs,
  }),
  requestUpdate: (formData) => apiClient.post("/kyc/update-request", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: FREE_BACKEND_CONFIG.uploadTimeoutMs,
  }),
  getStatus: () => apiClient.get("/kyc/status"),
  requestEmailVerification: (data) => apiClient.post("/kyc/email-verification/request", data),
  confirmEmailVerification: (data) => apiClient.post("/kyc/email-verification/confirm", data),
};

// Admin KYC API
export const adminKycAPI = {
  list: () => apiClient.get("/admin/kyc"),
  listUpdates: () => apiClient.get("/admin/kyc/updates"),
  proposeUpdate: (requestId, notes = "") => apiClient.post(`/admin/kyc/updates/${requestId}/propose`, { notes }),
  approveUpdate: (requestId, notes = "") => apiClient.post(`/admin/kyc/updates/${requestId}/approve`, { notes }),
  getUpdatePhotoUrl: (requestId, type) => {
    const token = localStorage.getItem("mmj_token");
    return `${API}/admin/kyc/updates/${requestId}/photo/${type}?auth=${token}`;
  },
  validate: (kycId, approved, reason) =>
    apiClient.post(`/admin/kyc/${kycId}/validate?approved=${approved}&rejection_reason=${encodeURIComponent(reason || "")}`),
  getPhotoUrl: (kycId, type) => {
    const token = localStorage.getItem("mmj_token");
    return `${API}/kyc/${kycId}/photo/${type}?auth=${token}`;
  },
  getActivationPaymentReceiptUrl: (kycId) => {
    const token = localStorage.getItem("mmj_token");
    return `${API}/kyc/${kycId}/activation-payment-receipt?auth=${token}`;
  },
  getAgreementPdfUrl: (kycId) => {
    const token = localStorage.getItem("mmj_token");
    return `${API}/kyc/${kycId}/agreement-pdf?auth=${token}`;
  },
  sendMessage: (kycId, message) => apiClient.post(`/kyc/messages/${kycId}?message=${encodeURIComponent(message)}`),
  getMessages: (kycId) => apiClient.get(`/kyc/messages/${kycId}`),
};

// Analytics API
export const analyticsAPI = {
  getBonuses: () => apiClient.get("/analytics/bonuses"),
  getMonthly: (month) => apiClient.get("/analytics/monthly", { params: month ? { month } : {} }),
  exportMonthly: (month) => apiClient.get(`/analytics/monthly/${month}/export`, { responseType: "blob" }),
};

// Files API
export const getFileUrl = (path) => {
  const token = localStorage.getItem("mmj_token");
  return `${API}/files/${path}?auth=${token}`;
};

export const getPublicCoverUrl = (path) => `${API}/public/covers/${path}`;
export const getPublicAuthorPhotoUrl = (path) => `${API}/public/author-photos/${path}`;

export default apiClient;
